// @ts-check

/**
 * ZEP-443 안전망: dead key 빌드타임 제외(i18n-exclude-keys.json)로 잘못 빠진 키가
 * 실제 prod 화면에서 호출되면 Sentry 에 경고를 남긴다. 클라이언트 + PRD 한정.
 *
 * 보고는 **한 틱 뒤에 다시 확인하고** 한다. next-i18next 는 i18n 인스턴스를 재사용하면서
 * 새 로케일 리소스는 렌더 중에 주입하고 `changeLanguage` 는 layout effect 에서 부른다.
 * 그 사이 한 렌더는 이전 언어로 조회돼 목적지 페이지 ns 가 통째로 미스로 잡힌다
 * (layout effect 라 paint 전에 교정된다 = 유저는 raw key 를 못 본다). 지금 풀리는 키는
 * 그 전환 프레임 오탐이므로 보고하지 않는다. 진짜 죽은 키는 나중에도 안 풀리니 그대로 보고된다.
 *
 * 판정만 여기서 하고 전송은 `_app` 이 등록한다 — 이 파일은 플레인 CJS 여야 해서
 * (next-i18next 가 서버에서 config 를 그대로 require 한다) Sentry 를 직접 import 할 수 없다.
 */

const isBrowser = typeof window !== 'undefined';

/**
 * 전환 프레임 재확인에 쓸 i18n 인스턴스. 없으면 재확인 없이 그대로 보고한다.
 * @type {import('i18next').i18n | null}
 */
let i18nRef = null;
/**
 * 실제 전송 함수. `_app` 이 등록하기 전에는 보고하지 않는다.
 * @type {CaptureMissingKey | null}
 */
let captureRef = null;

/** 실제로 보고한 키 — 세션당 1회로 묶는다. */
const reportedKeys = new Set();
/** 재확인 예약이 떠 있는 키 — 같은 틱 중복 예약을 막는다. */
const pendingKeys = new Set();

/**
 * 동시에 떠 있는 재확인 타이머 상한. 누적이 아니라 in-flight 기준이라
 * 타이머가 끝나면 자리가 돌아온다 — 세션 중 안전망이 영구히 꺼지지 않는다.
 */
const MAX_PENDING_CHECKS = 300;

/**
 * @typedef {(message: string, context: {
 *   ns: string, key: string, missedLocale: string, settledLocale: string,
 * }) => void} CaptureMissingKey
 */

/**
 * 누락 키 보고에 필요한 런타임 의존성을 등록한다. `_app` 에서 호출.
 * @param {{
 *   i18n?: import('i18next').i18n | null,
 *   captureMissingKey?: CaptureMissingKey | null,
 * }} deps
 */
function registerMissingKeyReporter(deps) {
  i18nRef = deps.i18n ?? null;
  if (deps.captureMissingKey) captureRef = deps.captureMissingKey;
}

/**
 * @type {NonNullable<import('next-i18next').UserConfig['missingKeyHandler']>}
 */
const missingKeyHandler = (...args) => {
  // i18next 콜백 시그니처: (lngs, ns, key, fallbackValue, updateMissing, options)
  const [lngs, ns, key, fallbackValue, , options] = args;
  if (!isBrowser) return;
  if (process.env.NEXT_PUBLIC_STAGE !== 'PRD') return;
  // t(key, { defaultValue }) 또는 <Trans> children 처럼 의도된 폴백이 있으면 유저는
  // 정상 텍스트를 보므로 보고하지 않는다. fallbackValue 가 비었거나 key 와 같을 때만
  // = 진짜 raw key 가 화면에 노출되는 경우 → 보고 대상.
  const hasIntendedFallback =
    (options && options.defaultValue != null) ||
    (typeof fallbackValue === 'string' &&
      fallbackValue !== '' &&
      fallbackValue !== key);
  if (hasIntendedFallback) return;

  const id = `${ns}:${key}`;
  if (reportedKeys.has(id) || pendingKeys.has(id)) return;
  if (pendingKeys.size >= MAX_PENDING_CHECKS) return;

  pendingKeys.add(id);
  const missedLocale = String(lngs && lngs[0]);

  setTimeout(() => {
    pendingKeys.delete(id);
    if (reportedKeys.has(id)) return;
    // ns 는 i18next 가 넘겨준 런타임 문자열이라 typed Namespace 로 좁힐 수 없다.
    const lookup = { ns: /** @type {never} */ (ns), lng: i18nRef?.language };
    if (i18nRef && i18nRef.exists(key, lookup)) return;
    if (!captureRef) return;
    reportedKeys.add(id);
    captureRef(`i18n missing key: ${id}`, {
      ns,
      key,
      missedLocale,
      settledLocale: i18nRef ? i18nRef.language : 'unknown',
    });
  }, 0);
};

module.exports = { missingKeyHandler, registerMissingKeyReporter };
